import { useEffect, useState } from "react";
import { ethers } from "ethers";
import { supabase } from "@/supabase/client";
import erc20Abi from "@/components/abi/erc20ABI.json";

const tokenAddress = "0x0A10D108E2d81ccC793E37b56206C84bF96ddc57"; // XSEED Token contract address

const provider = new ethers.providers.JsonRpcProvider(
  "https://api.avax.network/ext/bc/C/rpc"
);

const useTokenBalance = (walletAddress) => {
  const [tokenBalance, setTokenBalance] = useState("0");
  const [tokenSymbol, setTokenSymbol] = useState("XSEED");
  const [loading, setLoading] = useState(false);
  const [stakedTokenBalance, setStakedTokenBalance] = useState("0");

  const stakingAbi = [
    {
      inputs: [
        { internalType: "address", name: "user", type: "address" },
        { internalType: "uint256", name: "poolId", type: "uint256" },
      ],
      name: "getUserInfo",
      outputs: [
        {
          components: [
            { internalType: "uint256", name: "amount", type: "uint256" },
            { internalType: "uint256", name: "rewardDebt", type: "uint256" },
          ],
          internalType: "struct StakingPool.UserInfo",
          name: "",
          type: "tuple",
        },
      ],
      stateMutability: "view",
      type: "function",
    },
  ];

  useEffect(() => {
    const fetchTokenData = async () => {
      if (!walletAddress) {
        // console.log(
        //   "[useTokenBalance] ⚠️ No wallet address provided. Skipping fetch."
        // );
        return;
      }

      setLoading(true);

      try {
        // console.log(
        //   `[useTokenBalance] 🔄 Fetching on-chain data for wallet: ${walletAddress}`
        // );
        // Get token balance using RPC provider directly
        const balance = await getTokenBalance(walletAddress);
        setTokenBalance(balance);
        // console.log(`[useTokenBalance] 💰 Token balance: ${balance}`);

        // Get token symbol
        const symbol = await getTokenSymbol();
        setTokenSymbol(symbol);
        // console.log(`[useTokenBalance] 🏷️ Token symbol: ${symbol}`);

        // Get total staked token amount across active pools
        // console.log(
        //   "[useTokenBalance] 📦 Fetching staked balances across active pools..."
        // );
        const staked = await getTotalStakedForWallet(walletAddress);
        setStakedTokenBalance(staked);
        // console.log(`[useTokenBalance] 🥩 Total staked balance: ${staked}`);

        // Combine wallet balance + staked balance as the displayed tokenBalance
        const numericWallet =
          parseFloat(String(balance).replace(/,/g, "")) || 0;
        const numericStaked = parseFloat(String(staked).replace(/,/g, "")) || 0;
        const combinedTotal = numericWallet + numericStaked;
        const combinedFormatted =
          combinedTotal === 0
            ? "0"
            : combinedTotal.toLocaleString("en-US", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              });
        setTokenBalance(combinedFormatted);
        // console.log(
        //   `[useTokenBalance] 🧮 Combined wallet + staked balance: ${combinedFormatted}`
        // );
      } catch (error) {
        console.error("Error fetching token data:", error);
        setTokenBalance("0");
        setStakedTokenBalance("0");
      }

      setLoading(false);
    };

    fetchTokenData();
  }, [walletAddress]);

  const getTokenBalance = async (userAddress) => {
    const contract = new ethers.Contract(tokenAddress, erc20Abi, provider);
    try {
      const balance = await contract.balanceOf(userAddress);

      const decimals = await contract.decimals();

      const formattedBalance = ethers.utils.formatUnits(balance, decimals);

      // If balance is 0, return "0" without decimals
      if (parseFloat(formattedBalance) === 0) return "0";

      // Otherwise format with commas and 2 decimal places
      const formattedWithCommas = parseFloat(formattedBalance).toLocaleString(
        "en-US",
        {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }
      );

      return formattedWithCommas;
    } catch (error) {
      console.error(`Error calling balanceOf on contract:`, error);
      throw error;
    }
  };

  const fetchActiveStakingPools = async () => {
    try {
      // console.log(
      //   "[useTokenBalance] 📡 Querying Supabase for active staking pools..."
      // );
      const { data, error } = await supabase
        .from("staking_pools")
        .select("contract_id, pool_id, status")
        .eq("status", "active");
      if (error) {
        console.error("Error fetching staking pools:", error.message);
        return [];
      }
      const pools = data || [];
      // console.log(`[useTokenBalance] 📚 Found ${pools.length} active pool(s).`);
      return pools;
    } catch (err) {
      console.error("Unexpected error fetching staking pools:", err);
      return [];
    }
  };

  const getTotalStakedForWallet = async (userAddress) => {
    try {
      const pools = await fetchActiveStakingPools();
      if (!pools || pools.length === 0) return "0";

      let total = ethers.BigNumber.from(0);

      // Query each pool sequentially to avoid RPC throttling
      // console.log(
      //   `[useTokenBalance] 🚀 Fetching staked amounts for ${pools.length} pool(s) for ${userAddress}...`
      // );
      for (const pool of pools) {
        try {
          const stakingContract = new ethers.Contract(
            pool.contract_id,
            stakingAbi,
            provider
          );
          // console.log(
          //   `[useTokenBalance] 🔎 Calling getUserInfo(poolId=${pool.pool_id}) on ${pool.contract_id}...`
          // );
          const userInfo = await stakingContract.getUserInfo(
            userAddress,
            pool.pool_id
          );
          const amount = userInfo?.amount ?? ethers.BigNumber.from(0);
          total = total.add(amount);
          const formattedPerPool = ethers.utils.formatUnits(amount, 18);
          // console.log(
          //   `[useTokenBalance] ✅ Pool ${pool.pool_id} staked amount: ${formattedPerPool}`
          // );
        } catch (innerErr) {
          console.error(
            `[useTokenBalance] ❌ Pool ${pool.pool_id} fetch failed:`,
            innerErr
          );
        }
      }

      // Assume 18 decimals for staking amount unless specified differently by pool/token
      const formatted = ethers.utils.formatUnits(total, 18);
      // console.log(
      //   `[useTokenBalance] 🧮 Aggregated staked total (raw): ${total.toString()} | (formatted): ${formatted}`
      // );

      if (parseFloat(formatted) === 0) return "0";

      return parseFloat(formatted).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    } catch (error) {
      console.error(
        "[useTokenBalance] 💥 Error calculating total staked:",
        error
      );
      return "0";
    }
  };

  const getTokenSymbol = async () => {
    const contract = new ethers.Contract(tokenAddress, erc20Abi, provider);
    try {
      const symbol = await contract.symbol();
      return symbol;
    } catch (error) {
      console.error(`Error calling symbol on contract:`, error);
      throw error;
    }
  };

  return { tokenBalance, tokenSymbol, stakedTokenBalance, loading };
};

export default useTokenBalance;
